import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import {
  Edit as FormsIcon,
  Tablet as DevicesIcon,
  AssignmentTurnedIn as ResponsesIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSupabase } from '../contexts/SupabaseContext';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          sx={{
            backgroundColor: color,
            borderRadius: '50%',
            p: 1,
            mr: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" component="div" sx={{ mb: 1 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const { supabase } = useSupabase();

  // Fetch statistics
  const { data: stats, isLoading } = useQuery('dashboard-stats', async () => {
    const [
      templatesResult,
      devicesResult,
      responsesResult,
    ] = await Promise.all([
      supabase.from('form_templates').select('id', { count: 'exact' }),
      supabase.from('devices').select('id', { count: 'exact' }),
      supabase.from('form_responses').select('id', { count: 'exact' }),
    ]);

    return {
      templates: templatesResult.count || 0,
      devices: devicesResult.count || 0,
      responses: responsesResult.count || 0,
    };
  });

  if (isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  const statCards = [
    {
      title: 'Form Templates',
      value: stats?.templates || 0,
      icon: <FormsIcon sx={{ color: 'white' }} />,
      color: '#5BA3B3', // Light teal
      subtitle: 'Available templates',
    },
    {
      title: 'Devices',
      value: stats?.devices || 0,
      icon: <DevicesIcon sx={{ color: 'white' }} />,
      color: '#A3C893', // Light sage
      subtitle: 'Registered iPads',
    },
    {
      title: 'Form Responses',
      value: stats?.responses || 0,
      icon: <ResponsesIcon sx={{ color: 'white' }} />,
      color: '#6B8E8E', // Muted teal
      subtitle: 'Total submissions',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>
      
      <Grid container spacing={3}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.title}>
            <StatCard {...card} />
          </Grid>
        ))}
      </Grid>

    </Box>
  );
};

export default Dashboard;
